import type { App } from 'vue'
import type { SensitiveApprovalResponse } from './types'
import ElementPlus from 'element-plus'
import { createApp } from 'vue'
import SensitiveApprovalApplyDialog from './SensitiveApprovalApplyDialog.vue'

export class SensitiveApprovalFlowCanceledError extends Error {
  silent = true

  constructor() {
    super('敏感操作审核申请已取消')
    this.name = 'SensitiveApprovalFlowCanceledError'
  }
}

export class SensitiveApprovalSubmittedError extends Error {
  silent = true

  constructor(public readonly approval: SensitiveApprovalResponse) {
    super(approval.message || '审核申请已提交')
    this.name = 'SensitiveApprovalSubmittedError'
  }
}

export class SensitiveApprovalDuplicatePendingError extends Error {
  silent = true
  sensitiveApprovalDuplicatePending = true

  constructor(message = '当前业务对象已有待审核操作') {
    super(message)
    this.name = 'SensitiveApprovalDuplicatePendingError'
  }
}

export function isSensitiveApprovalDuplicatePendingError(error: unknown) {
  return Boolean(
    error instanceof SensitiveApprovalDuplicatePendingError ||
    (
      typeof error === 'object' &&
      error !== null &&
      (
        (error as { sensitiveApprovalDuplicatePending?: unknown }).sensitiveApprovalDuplicatePending === true ||
        (error as { name?: unknown }).name === 'SensitiveApprovalDuplicatePendingError'
      )
    ),
  )
}

export function isSensitiveApprovalDuplicatePendingMessage(message?: string) {
  return Boolean(message && (/已有待审核/.test(message) || /正在审核中/.test(message)))
}

export function isSensitiveApprovalSilentError(error: unknown) {
  return Boolean(
    error instanceof SensitiveApprovalFlowCanceledError ||
    error instanceof SensitiveApprovalSubmittedError ||
    error instanceof SensitiveApprovalDuplicatePendingError ||
    (typeof error === 'object' && error !== null && (error as { silent?: unknown }).silent === true),
  )
}

export function openSensitiveApprovalApplyDialog<T>(
  approval: SensitiveApprovalResponse,
  submitter: (_reason: string) => Promise<T>,
): Promise<T> {
  if (typeof document === 'undefined') {
    return Promise.reject(new Error('当前环境不支持审核申请弹窗'))
  }

  const container = document.createElement('div')
  document.body.appendChild(container)

  let app: App<Element> | undefined
  const cleanup = () => {
    app?.unmount()
    container.remove()
  }

  return new Promise<T>((resolve, reject) => {
    app = createApp(SensitiveApprovalApplyDialog, {
      approval,
      submitter,
      onResolved: (result: T) => {
        cleanup()
        resolve(result)
      },
      onCancel: () => {
        cleanup()
        reject(new SensitiveApprovalFlowCanceledError())
      },
    })
    app.use(ElementPlus)
    app.mount(container)
  })
}
